import { ComponentType, useEffect, useState } from "react";
import { FilePreviewComponentProps } from "./FilePreview.types";
import { FilePreviewLoading } from "./FilePreviewLoading";
import { FilePreviewError } from "./FilePreviewError";
import assert from "assert";
import { FilePreviewTooLarge } from "./FilePreviewTooLarge";

/** Maximum support file preview size */
const MAX_FILE_PREVIEW_SIZE = Math.pow(20, 7); // 20 MB;

/**
 * Gets the file size of a preview using a presigned URL.
 * @param url The preview's presigned URL.
 * @returns The previews's file size.
 * @throws An error if preview size could not be read.
 *
 */
export const getPreviewSize = async (url: string) => {
  const response = await fetch(url);
  assert(response.ok);
  const contentLength = response.headers.get("content-length");
  assert(contentLength !== null);
  return parseInt(contentLength);
};

/**
 * HOC that wraps a file preview component and checks whether it's file size
 * exceed the limit we want to load into memory.
 * @param FilePreviewComponent The file preview component to enhance.
 * @returns The enhanced component.
 */
export const withFilePreviewSizeLimit = (
  FilePreviewComponent: ComponentType<FilePreviewComponentProps>,
) => {
  const EnhancedComponent = (props: FilePreviewComponentProps) => {
    const [size, setSize] = useState<number>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<Error>();

    useEffect(() => {
      setIsLoading(true);
      void getPreviewSize(props.preview.url)
        .then(setSize)
        .catch(setError)
        .finally(() => setIsLoading(false));
    }, [props.preview.url]);

    if (isLoading) {
      return <FilePreviewLoading />;
    }

    if (size === undefined || error !== undefined) {
      return <FilePreviewError />;
    }

    if (size > MAX_FILE_PREVIEW_SIZE) {
      return <FilePreviewTooLarge {...props} size={size} />;
    }

    return <FilePreviewComponent {...props} />;
  };
  return EnhancedComponent;
};
